import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, TextField, Button, Typography,
  Autocomplete, MenuItem, Select, FormControl, InputLabel,
  Chip, IconButton, Divider, CircularProgress, Alert
} from '@mui/material';
import { ArrowBack, Save, Transform, Send, Email } from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import LineItemsEditor from '../components/LineItemsEditor';
import { STATUS_COLORS, STATUS_LABELS, fmt } from '../utils';
import { useSnackbar } from 'notistack';
import SendQuoteEmailDialog from '../components/SendQuoteEmailDialog';


// Computes the effective display status considering expiry
function getEffectiveStatus(status, validUntil) {
  if (['facturada', 'cancelada', 'aceptada'].includes(status)) return status;
  if (validUntil && new Date(validUntil) < new Date()) return 'vencida';
  return status;
}

const DEFAULT_FORM = {
  client: null, seller: '', items: [{ description: '', qty: 1, unitPrice: 0, discount: 0, taxRate: 16 }],
  paymentTerms: 'COD', currency: 'MXN', validUntil: '',
  publicNote: 'Precios en moneda MXN\nCostos sujetos a cambio sin previo aviso',
  privateNote: '', footerNote: 'Se requiere orden de compra para ser procesada\nNo se aceptan devoluciones\nTiempo de entrega de 5 a 7 días hábiles. Sujeto a disponibilidad de inventario',
  status: 'borrador'
};

export default function QuoteFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !id;

  const [form, setForm] = useState(DEFAULT_FORM);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  useEffect(() => {
    API.get('/auth/users').then(r => setUsers(r.data));
    const preClientId = searchParams.get('client');
    if (preClientId) {
      API.get(`/clients/${preClientId}`).then(r => setForm(f => ({ ...f, client: r.data })));
    }
    if (!isNew) {
      setLoading(true);
      API.get(`/quotes/${id}`).then(r => {
        setQuote(r.data);
        setForm({ ...DEFAULT_FORM, ...r.data, client: r.data.client });
      }).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  useEffect(() => {
    API.get('/clients', { params: { search: clientSearch, limit: 10 } }).then(r => setClients(r.data.clients));
  }, [clientSearch]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSave = async (newStatus) => {
    if (!form.client) return enqueueSnackbar('Selecciona un cliente', { variant: 'error' });
    setSaving(true);
    try {
      const payload = { ...form, client: form.client?._id || form.client, status: newStatus || form.status, seller: form.seller || user?.id };
      if (isNew) await API.post('/quotes', payload);
      else await API.put(`/quotes/${id}`, payload);
      enqueueSnackbar(isNew ? 'Cotización creada' : 'Cotización guardada', { variant: 'success' });
      navigate('/cotizaciones');
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async () => {
    if (!window.confirm('¿Convertir esta cotización a factura?')) return;
    try {
      const { data } = await API.post(`/quotes/${id}/convert`);
      enqueueSnackbar(`Factura ${data.invoice.number} generada`, { variant: 'success' });
      navigate(`/facturas/${data.invoice._id}/editar`);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al convertir', { variant: 'error' });
    }
  };

  const totals = form.items.reduce((acc, item) => {
    const base = (item.qty || 0) * (item.unitPrice || 0);
    const disc = base * ((item.discount || 0) / 100);
    const taxable = base - disc;
    const tax = taxable * ((item.taxRate || 0) / 100);
    return { sub: acc.sub + taxable, tax: acc.tax + tax };
  }, { sub: 0, tax: 0 });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;

  const isReadonly = ['facturada', 'cancelada'].includes(quote?.status);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/cotizaciones')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={800}>{isNew ? 'Nueva cotización' : `Cotización ${quote?.number}`}</Typography>
          {quote && <Chip label={STATUS_LABELS[quote.status]} color={STATUS_COLORS[quote.status]} size="small" sx={{ mt: 0.5 }} />}
        </Box>
        {!isReadonly && <>
          <Button variant="outlined" startIcon={<Save />} onClick={() => handleSave()} disabled={saving}>Guardar</Button>
          <Button variant="outlined" color="info" onClick={() => handleSave('enviada')} disabled={saving}>
            <Send sx={{ mr: 0.5, fontSize: '1rem' }} /> Marcar enviada
          </Button>
        </>}
        {!isNew && quote && (
          <Button variant="outlined" color="primary" startIcon={<Email />} onClick={() => setEmailDialogOpen(true)}>
            Enviar por correo
          </Button>
        )}
        {!isNew && ['borrador','enviada','aceptada'].includes(quote?.status) && (
          <Button variant="contained" color="success" startIcon={<Transform />} onClick={handleConvert}>
            Convertir a factura
          </Button>
        )}
      </Box>

      {isReadonly && <Alert severity="info" sx={{ mb: 2 }}>Esta cotización está {STATUS_LABELS[quote?.status]?.toLowerCase()} y no puede editarse.</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={o => o.name || ''}
                    value={form.client}
                    onChange={(_, val) => set('client', val)}
                    onInputChange={(_, val) => setClientSearch(val)}
                    disabled={isReadonly}
                    renderOption={(props, o) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.rfc || o.email}</Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={params => <TextField {...params} label="Cliente *" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vendedor</InputLabel>
                    <Select value={form.seller || user?.id || ''} onChange={e => set('seller', e.target.value)} label="Vendedor" disabled={isReadonly}>
                      {users.map(u => <MenuItem key={u._id} value={u._id}>{u.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Cond. de pago" value={form.paymentTerms}
                    onChange={e => set('paymentTerms', e.target.value)} disabled={isReadonly} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth label="Válida hasta" type="date"
                    value={form.validUntil ? new Date(form.validUntil).toISOString().split('T')[0] : ''}
                    onChange={e => set('validUntil', e.target.value)}
                    disabled={isReadonly}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>Artículos</Typography>
              <LineItemsEditor items={form.items} onChange={v => set('items', v)} disabled={isReadonly} />
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nota pública" value={form.publicNote}
                    onChange={e => set('publicNote', e.target.value)} multiline rows={3} disabled={isReadonly} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nota al pie" value={form.footerNote}
                    onChange={e => set('footerNote', e.target.value)} multiline rows={3} disabled={isReadonly} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Nota privada (interna)" value={form.privateNote}
                    onChange={e => set('privateNote', e.target.value)} multiline rows={2} disabled={isReadonly} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>Resumen</Typography>
              {form.client && (
                <Box sx={{ mb: 2, p: 1.5, background: 'rgba(107,33,168,0.05)', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Facturar a:</Typography>
                  <Typography variant="body2" fontWeight={600}>{form.client.name}</Typography>
                  <Typography variant="caption">{form.client.rfc}</Typography>
                </Box>
              )}
              {/* Status selector */}
              {!isNew && (() => {
                const effStatus = getEffectiveStatus(form.status, form.validUntil);
                const isExpired = effStatus === 'vencida';
                const statusColor = {
                  borrador: '#6B7280', enviada: '#3B82F6', aceptada: '#10B981',
                  rechazada: '#EF4444', facturada: '#8B5CF6', cancelada: '#9CA3AF', vencida: '#F59E0B'
                }[effStatus] || '#6B7280';
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Estado</Typography>
                    {isReadonly ? (
                      <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', background: 'rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={700} sx={{ color: statusColor }}>
                          {STATUS_LABELS[effStatus] || effStatus}
                        </Typography>
                      </Box>
                    ) : (
                      <FormControl fullWidth size="small">
                        <Select
                          value={isExpired ? 'vencida' : form.status}
                          onChange={e => { if (e.target.value !== 'vencida') set('status', e.target.value); }}
                          sx={{
                            '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 },
                            ...(isExpired && { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#F59E0B' } })
                          }}
                        >
                          {[
                            { value: 'borrador', label: 'Borrador', color: '#6B7280' },
                            { value: 'enviada', label: 'Enviada', color: '#3B82F6' },
                            { value: 'aceptada', label: 'Aceptada', color: '#10B981' },
                            { value: 'rechazada', label: 'Rechazada', color: '#EF4444' },
                            ...(isExpired ? [{ value: 'vencida', label: 'Vencida (automático)', color: '#F59E0B' }] : [])
                          ].map(s => (
                            <MenuItem key={s.value} value={s.value} disabled={s.value === 'vencida'}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                                {s.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {form.validUntil && !isExpired && (
                      <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                        Válida hasta: {fmt.date(form.validUntil)}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={600}>{fmt.currency(totals.sub)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">IVA:</Typography>
                <Typography variant="body2" fontWeight={600}>{fmt.currency(totals.tax)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography variant="h6" fontWeight={800}>Total:</Typography>
                <Typography variant="h6" fontWeight={800} color="primary.main">{fmt.currency(totals.sub + totals.tax)}</Typography>
              </Box>

              {!isReadonly && (
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button fullWidth variant="contained" onClick={() => handleSave()} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
                    {isNew ? 'Crear cotización' : 'Guardar cambios'}
                  </Button>
                  {!isNew && (
                    <Button fullWidth variant="outlined" color="success" onClick={handleConvert} startIcon={<Transform />}>
                      Convertir a factura
                    </Button>
                  )}
                </Box>
              )}

              {/* Linked invoices */}
              {quote?.invoices?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing="0.1em" color="primary.main">
                    Facturas generadas
                  </Typography>
                  {quote.invoices.map(inv => (
                    <Box key={inv._id} sx={{
                      display: 'flex', justifyContent: 'space-between', mt: 1,
                      cursor: 'pointer', p: 1, borderRadius: 1, '&:hover': { background: '#F5F3FF' }
                    }}
                      onClick={() => navigate(`/facturas/${inv._id}/editar`)}
                    >
                      <Typography variant="body2" fontWeight={600} color="primary.main">{inv.number}</Typography>
                      <Chip label={STATUS_LABELS[inv.status]} color={STATUS_COLORS[inv.status]} size="small" />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <SendQuoteEmailDialog
        open={emailDialogOpen}
        quote={quote}
        onClose={() => setEmailDialogOpen(false)}
        onSent={() => {
          if (quote?.status === 'borrador') {
            setQuote(q => ({ ...q, status: 'enviada' }));
          }
        }}
      />
    </Box>
  );
}
